import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Check, X, Clock } from "lucide-react";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

/**
 * Pending upgrade requests — admin reviews bank transfer references submitted
 * by individual users on Subscription page, then approves (activates tier for
 * 30 days) or rejects (with an optional reason). Uses:
 *   GET  /admin/subscription-requests?status=pending
 *   POST /admin/subscription-requests/{id}/approve
 *   POST /admin/subscription-requests/{id}/reject
 */
export function SubscriptionRequestsTab() {
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [busyId, setBusyId] = useState(null);
    const [noteById, setNoteById] = useState({});
    const { sorted: sortedRows, sortKey, direction, requestSort } = useSortableData(rows, {
        accessors: {
            submitted: (r) => r.created_at || "",
            user: (r) => r.full_name || r.user_email || "",
            tier: (r) => r.tier || "",
            paid: (r) => Number(r.amount_paid_kes || 0),
            expected: (r) => Number(r.expected_kes || 0),
            ref: (r) => r.bank_reference || "",
            status: (r) => r.status || "",
        },
    });

    async function load() {
        try {
            const { data } = await api.get(`/admin/subscription-requests?status=${statusFilter}&limit=100`);
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load requests.");
        }
    }
    useEffect(() => { load(); }, [statusFilter]);   // eslint-disable-line react-hooks/exhaustive-deps

    async function decide(id, action) {
        setBusyId(id);
        try {
            await api.post(`/admin/subscription-requests/${id}/${action}`, {
                admin_note: (noteById[id] || "").trim() || null,
            });
            toast.success(action === "approve" ? "Upgrade approved — user tier activated." : "Request rejected.");
            setNoteById((m) => ({ ...m, [id]: "" }));
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Couldn't ${action} request.`);
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="admin-subscription-requests">
            <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">Subscription upgrade requests</h3>
                <div className="ml-auto flex items-center gap-1">
                    {["pending", "approved", "rejected", "all"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-2 py-1 rounded text-[11px] font-semibold border transition-colors ${
                                statusFilter === s
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "border-border text-muted-foreground hover:border-primary/50"
                            }`}
                            data-testid={`sub-req-filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            {rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                    <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    No {statusFilter} requests.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="border-b border-border">
                            <tr className="text-left text-muted-foreground">
                                <SortableTh label="Submitted" sortKey="submitted" currentKey={sortKey} direction={direction} onSort={requestSort} className="py-2 px-3" />
                                <SortableTh label="User" sortKey="user" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3" />
                                <SortableTh label="Tier" sortKey="tier" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3" />
                                <SortableTh label="Paid (KES)" sortKey="paid" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-3" />
                                <SortableTh label="Expected" sortKey="expected" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" className="px-3" />
                                <SortableTh label="Bank reference" sortKey="ref" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3" />
                                <SortableTh label="Status" sortKey="status" currentKey={sortKey} direction={direction} onSort={requestSort} className="px-3" />
                                <th className="px-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((r) => {
                                const decided = r.status !== "pending";
                                return (
                                    <tr key={r.id} className="border-b border-border/50 last:border-0 align-top">
                                        <td className="py-2 px-3 mono">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                                        <td className="px-3">
                                            <div className="font-semibold">{r.full_name || "—"}</div>
                                            <div className="text-[10px] text-muted-foreground mono">{r.user_email}</div>
                                        </td>
                                        <td className="px-3 font-bold uppercase">{r.tier}</td>
                                        <td className={`px-3 text-right mono ${r.amount_paid_kes < r.expected_kes ? "text-red-500" : ""}`}>
                                            {r.amount_paid_kes.toLocaleString()}
                                        </td>
                                        <td className="px-3 text-right mono text-muted-foreground">{r.expected_kes.toLocaleString()}</td>
                                        <td className="px-3 mono">{r.bank_reference}</td>
                                        <td className="px-3">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                r.status === "pending"  ? "bg-yellow-500/15 text-yellow-600" :
                                                r.status === "approved" ? "bg-emerald-500/15 text-emerald-600" :
                                                                          "bg-red-500/15 text-red-600"
                                            }`}>{r.status}</span>
                                        </td>
                                        <td className="px-3">
                                            {decided ? (
                                                <div className="text-[10px] text-muted-foreground max-w-[180px]">
                                                    by <span className="mono">{r.decided_by || "—"}</span>
                                                    {r.admin_note && <div className="italic mt-0.5">"{r.admin_note}"</div>}
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-1 min-w-[180px]">
                                                    <input
                                                        type="text"
                                                        placeholder="Note (optional)"
                                                        value={noteById[r.id] || ""}
                                                        onChange={(e) => setNoteById((m) => ({ ...m, [r.id]: e.target.value }))}
                                                        className="w-full h-6 rounded border border-border bg-background px-1.5 text-[10px]"
                                                        data-testid={`sub-req-note-${r.id}`}
                                                    />
                                                    <div className="flex gap-1">
                                                        <Button
                                                            size="sm"
                                                            className="h-6 text-[10px] flex-1"
                                                            disabled={busyId === r.id}
                                                            onClick={() => decide(r.id, "approve")}
                                                            data-testid={`sub-req-approve-${r.id}`}
                                                        >
                                                            <Check className="w-3 h-3 mr-0.5" /> Approve
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-6 text-[10px] flex-1"
                                                            disabled={busyId === r.id}
                                                            onClick={() => decide(r.id, "reject")}
                                                            data-testid={`sub-req-reject-${r.id}`}
                                                        >
                                                            <X className="w-3 h-3 mr-0.5" /> Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
